<template>
  <v-container fluid class="ma-0 pa-0 fill-height ">
    <TaskPanel
      v-if="tasks"
      team="plating"
      :tasks-in="tasks"
      hidden
      :task-confirm-start="taskConfirmStart"
      v-on:close-start-confirm="taskConfirmStart=null"
      :task-confirm-stop="taskConfirmStop"
      v-on:close-stop-confirm="taskConfirmStop=null"
      :check-running-tasks="true"
      show-team-member-buttons
    />
    <v-dialog v-model="showPlatingDetail" max-width="1200">
      <v-card v-if="selectedMeal && showPlatingDetail">
        <v-card-title>
          {{ selectedMeal.name }}
          <v-chip class="ml-2" outlined v-if="platingDetailRestrictions" color="red">ALLERGY</v-chip>
          <v-spacer/>
          <v-btn icon @click="showPlatingDetail=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="4" v-if="platingDetailImageUrl">
              <v-img :src="platingDetailImageUrl"/>
            </v-col>
            <v-col :cols="platingDetailImageUrl ? 8 : 12">
              <Plating
                v-bind:key="selectedMeal.id"
                hide-header
                hide-controls
                read-only
                skip-url-sync
                dense
                smaller
                :restrictions="platingDetailRestrictions"
                :meal-id="selectedMeal.id"
                :production="production"
                :date="platingDetailDate"
                :dates="dates"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showMealDialog" max-width="900" scrollable>
      <v-card v-if="selectedMeal">
        <v-card-title>{{ selectedMeal.name }}
          {{ getMealCount(selectedMeal.id) }}CT +
          {{ getAllergyMealCount(selectedMeal.id) }}CT
          ({{ getMealCount(selectedMeal.id) + getAllergyMealCount(selectedMeal.id) }}CT)
          <v-spacer/>
          <span class="text-capitalize">{{ mealNameShort(selectedMeal) }}</span>
        </v-card-title>
        <template v-if="!enterShort">
          <v-alert type="warning" v-if="mealNameShort(selectedMeal).indexOf('LC')===0">
            Note: meal counts shown here include both low carb and diabetes meals!
          </v-alert>
          <v-alert v-if="!platingTaskMealMap[selectedMeal.id]" type="warning">
            Could not find task for meal
          </v-alert>
          <template v-if="platingTaskMealMap[selectedMeal.id]">
            <v-card-subtitle>
              {{ platingTaskMealMap[selectedMeal.id].description }}
            </v-card-subtitle>
            <v-card-text>
              <v-alert v-if="isDisconnected" type="warning">
                Disconnected from Server. Tasks will not update!
              </v-alert>

              <v-row>
                <v-col class="align-self-center text-center">
                  <h2 v-if="getSafeTimeLeft(selectedMeal)===0" style="color: red">
                    <v-icon color="red">mdi-skull-crossbones</v-icon>
                    Over 40 minutes by: {{ formatDuration(getExceededSafeTime(selectedMeal)) }}
                    <v-icon color="red">mdi-skull-crossbones</v-icon>
                  </h2>
                  <h2 v-if="getSafeTimeLeft(selectedMeal)">
                    <template v-if="isColdWarning(selectedMeal)">
                      <v-icon color="orange">mdi-skull-crossbones</v-icon>
                    </template>
                    <span style="color: green">SAFE TIME LEFT: {{
                        formatDuration(getSafeTimeLeft(selectedMeal))
                      }}</span>
                  </h2>
                </v-col>
                <v-col cols="3" v-if="!readOnly">
                  <v-text-field
                    label="Change Plating Rack"
                    v-model="platingTaskMealMap[selectedMeal.id].source.rack"
                    @blur="saveRack(platingTaskMealMap[selectedMeal.id], allergyPlatingTaskMealMap[selectedMeal.id])"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </template>
          <v-card-text>
            <v-row dense>
              <v-col v-if="platingTaskMealMap[selectedMeal.id]" class="d-flex line-card-col">
                <v-card class="d-flex flex-column flex-grow-1">
                  <v-card-title>
                    <v-spacer/>
                    Main Plating ({{ getMealCount(selectedMeal.id) }}CT)
                    <v-spacer/>
                    <template v-if="isMealPlated(selectedMeal.id)">
                      <v-chip outlined small color="green">M
                        <v-icon fab>mdi-check</v-icon>
                      </v-chip>
                    </template>
                    <template v-if="isStarted(selectedMeal.id)">
                      <v-chip outlined small color="orange">M
                        <v-icon fab>mdi-play</v-icon>
                      </v-chip>
                    </template>
                    <template v-if="isPaused(selectedMeal.id)">
                      <v-chip outlined small color="blue">M
                        <v-icon fab>mdi-pause</v-icon>
                      </v-chip>
                    </template>
                  </v-card-title>
                  <v-card-text v-if="selectedMeal.plating_rack" class="text-center">
                    <v-chip class="ml-2" outlined>
                      M{{ selectedMeal.plating_rack }}
                    </v-chip>
                  </v-card-text>
                  <v-card-text class="flex-grow-1">
                    <p class="text-center" style="font-weight: bold"
                       v-if="getElapsedTime(platingTaskMealMap[selectedMeal.id])">
                      Actual Time {{ formatDuration(getElapsedTime(platingTaskMealMap[selectedMeal.id])) }}
                    </p>
                    <p class="text-center">Estimate {{
                        formatDuration(getPlatingTimeEstimate(getMealCount(selectedMeal.id)))
                      }}
                      (1 person)
                    </p>

                    <p v-if="platingTaskMealMap[selectedMeal.id].note">
                      Note: {{ platingTaskMealMap[selectedMeal.id].note }}
                    </p>
                    <ul>
                      <li v-for="(e,index) of platingTaskMealMap[selectedMeal.id].execution" v-bind:key="index">
                        {{ formatTime(e.start) }}-{{ formatTime(e.stop) }}
                        ({{ formatDuration(asMilli(e.stop || now) - asMilli(e.start)) }})
                        - {{ e.team_members.map(t => t.name).join(', ') }}
                        <v-chip color="green" outlined v-if="!e.stop">Currently Working</v-chip>
                      </li>
                    </ul>
                  </v-card-text>
                  <v-divider/>
                  <v-card-actions class="flex-wrap line-card-actions">
                    <v-btn v-if="!isStarted(selectedMeal.id)" color="primary" large
                           @click="showMealDialog=false;  taskConfirmStart=platingTaskMealMap[selectedMeal.id]">
                      Start
                    </v-btn>
                    <template v-if="isStarted(selectedMeal.id)">
                      <v-btn color="success" large
                             @click="stopTask(platingTaskMealMap[selectedMeal.id], true)">
                        Done
                      </v-btn>
                      <v-btn color="warning" large
                             @click="stopTask(platingTaskMealMap[selectedMeal.id], false)">
                        Pause
                      </v-btn>
                    </template>
                  </v-card-actions>
                  <v-card-actions class="flex-wrap line-card-actions pt-0">
                    <v-btn text color="primary" @click="showPlatingDetailFor()">
                      <v-icon left>mdi-clipboard-list-outline</v-icon>
                      Plating Detail
                    </v-btn>
                    <v-btn v-if="isStarted(selectedMeal.id)" outlined color="error"
                           @click="showMealDialog=false;  taskConfirmStop=platingTaskMealMap[selectedMeal.id]">
                      Comment
                    </v-btn>
                    <v-btn outlined color="pink" @click="startShort('main')">Short</v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
              <v-col v-if="allergyPlatingTaskMealMap[selectedMeal.id]" class="d-flex line-card-col">
                <v-card class="d-flex flex-column flex-grow-1">
                  <v-card-title class="text-center">
                    <v-spacer/>
                    Allergy Plating ({{ getAllergyMealCount(selectedMeal.id) }}CT)
                    <v-spacer/>
                    <template v-if="isAllergyStarted(selectedMeal.id)">
                      <v-chip outlined small color="orange">A
                        <v-icon fab>mdi-play</v-icon>
                      </v-chip>
                    </template>
                    <template v-if="isAllergyComplete(selectedMeal.id)">
                      <v-chip outlined small color="green">A
                        <v-icon fab>mdi-check</v-icon>
                      </v-chip>
                    </template>
                    <template v-if="isAllergyPaused(selectedMeal.id)">
                      <v-chip outlined small color="blue">A
                        <v-icon fab>mdi-pause</v-icon>
                      </v-chip>
                    </template>

                  </v-card-title>
                  <v-card-text v-if="selectedMeal.plating_rack" class="text-center">
                    <v-chip
                      v-for="line of getAllergyLines(selectedMeal.id)"
                      v-bind:key="`${line}-${selectedMeal.id}`"
                      class="ml-2" outlined>
                      {{ line }}{{ selectedMeal.plating_rack }}
                    </v-chip>
                  </v-card-text>
                  <v-card-text class="flex-grow-1">
                    <p class="text-center" style="font-weight: bold"
                       v-if="getElapsedTime(allergyPlatingTaskMealMap[selectedMeal.id])">
                      Actual Time {{ formatDuration(getElapsedTime(allergyPlatingTaskMealMap[selectedMeal.id])) }}
                    </p>
                    <p class="text-center">Estimate
                      {{ formatDuration(getPlatingTimeEstimate(getAllergyMealCount(selectedMeal.id))) }}
                      (1 person)
                    </p>
                    <p v-if="allergyPlatingTaskMealMap[selectedMeal.id].note">
                      Note: {{ allergyPlatingTaskMealMap[selectedMeal.id].note }}
                    </p>
                    <ul>
                      <li v-for="(e,index) of allergyPlatingTaskMealMap[selectedMeal.id].execution" v-bind:key="index">
                        {{ formatTime(e.start) }}-{{ formatTime(e.stop) }}
                        ({{ formatDuration(asMilli(e.stop || now) - asMilli(e.start)) }})
                        - {{ e.team_members.map(t => t.name).join(', ') }}
                        <v-chip color="green" outlined v-if="!e.stop">Currently Working</v-chip>
                      </li>
                    </ul>
                  </v-card-text>
                  <v-divider/>
                  <v-card-actions class="flex-wrap line-card-actions">
                    <v-btn v-if="!isAllergyStarted(selectedMeal.id)" color="primary" large
                           @click="showMealDialog =false; taskConfirmStart = allergyPlatingTaskMealMap[selectedMeal.id]">
                      Start
                    </v-btn>
                    <template v-if="isAllergyStarted(selectedMeal.id)">
                      <v-btn color="success" large
                             @click="stopTask(allergyPlatingTaskMealMap[selectedMeal.id], true)">
                        Done
                      </v-btn>
                      <v-btn color="warning" large
                             @click="stopTask(allergyPlatingTaskMealMap[selectedMeal.id], false)">
                        Pause
                      </v-btn>
                    </template>
                  </v-card-actions>
                  <v-card-actions class="flex-wrap line-card-actions pt-0">
                    <v-btn text color="primary" @click="showPlatingDetailFor(true)">
                      <v-icon left>mdi-clipboard-list-outline</v-icon>
                      Plating Detail
                    </v-btn>
                    <v-btn v-if="isAllergyStarted(selectedMeal.id)" outlined color="error"
                           @click="showMealDialog =false; taskConfirmStop = allergyPlatingTaskMealMap[selectedMeal.id]">
                      Comment
                    </v-btn>
                    <v-btn outlined color="pink" @click="startShort('allergy')">Short</v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
        <v-card-text v-if="enterShort && !shortComponent">
          <h3 class="mb-1">{{ shortPickerTitle }}</h3>
          <div class="d-flex align-center mb-3">
            <span class="grey--text">Tap a component to request a short or update an existing request.</span>
            <v-spacer/>
            <v-switch v-model="showGreyAllergies" dense hide-details class="mt-0 ml-2"
                      label="other allergies"/>
          </div>
          <v-row v-if="shortLine === 'allergy'" dense>
            <v-col><h4>Allergy Line</h4></v-col>
            <v-col><h4>Substitutes</h4></v-col>
          </v-row>
          <v-row v-for="row of getShortPickerRows(selectedMeal.id)" v-bind:key="row.key" class="short-picker-row">
            <v-col v-for="cell of row.cells" v-bind:key="cell.key" class="short-picker-cell">
              <template v-if="cell.task">
                <div>
                  <v-btn @click="shortComponent=cell.task" :color="cell.isSub ? 'warning' : 'primary'">
                    <span style="max-width: 200px; overflow: clip">{{ cell.task.source.batch }}</span>
                  </v-btn>
                  <span class="ml-2"><span v-if="cell.isSub">SUB for </span>{{ cell.task.description }}</span>
                  <v-chip v-if="cell.task.source.mealId !== selectedMeal.id"
                          x-small outlined color="purple" class="ml-1">
                    Diabetes
                  </v-chip>
                </div>
                <div v-if="getCellDetectedAllergies(cell).length || (showGreyAllergies && getCellOtherAllergies(cell).length)"
                     class="short-picker-allergies">
                  <!-- red = detected in this component (drives the sub); grey = riding the
                       allergy line without being in this component -->
                  <v-chip v-for="name of getCellDetectedAllergies(cell)" v-bind:key="name"
                          x-small outlined color="red" class="mr-1">
                    {{ name }}
                  </v-chip>
                  <template v-if="showGreyAllergies && getCellOtherAllergies(cell).length">
                    <span class="grey--text mr-1">not in component:</span>
                    <v-chip v-for="name of getCellOtherAllergies(cell)" v-bind:key="`ni-${name}`"
                            x-small outlined color="grey" class="mr-1">
                      {{ name }}
                    </v-chip>
                  </template>
                </div>
                <div v-if="getShortTaskFor(cell.task)" class="short-picker-request">
                  <v-chip small :color="getShortTaskStatusColor(cell.task)" dark class="mr-2">
                    {{ getShortTaskStatus(cell.task) }}
                  </v-chip>
                  <strong>{{ getShortAmount(getShortTaskFor(cell.task)) }}</strong>
                  ({{ getShortCountString(getShortTaskFor(cell.task)) }})
                  <span v-if="getShortAge(getShortTaskFor(cell.task))" class="ml-2">
                    requested {{ getShortAge(getShortTaskFor(cell.task)) }}
                  </span>
                  <span v-if="getTeamMembers(getShortTaskFor(cell.task))" class="ml-2">
                    by {{ getTeamMembers(getShortTaskFor(cell.task)).join(', ') }}
                  </span>
                </div>
              </template>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-if="enterShort && shortComponent">
          <h4>How much
            <span v-if="shortComponent.source.line ==='S'">SUB for </span>
            {{ shortComponent.description }} do you need?
            <v-chip v-if="selectedMeal && shortComponent.source.mealId !== selectedMeal.id"
                    x-small outlined color="purple" class="ml-1">
              Diabetes
            </v-chip>
          </h4>
          <v-alert v-if="getShortTaskFor(shortComponent) && !isShortTaskNonEditable(shortComponent)" type="warning" dense class="mb-2">
            A short request already exists for this component. You can update the amount below.
          </v-alert>
          <!-- a sub short is for an allergy: the tabs are the allergy combinations the orders
               for this meal need from this component, each with its own amounts -->
          <div v-if="shortComponent.source.line === 'S'" class="short-notes">
            <h4>Are you missing a specific allergy?
              <span v-if="shortOrdersLoading" class="grey--text font-weight-regular">loading orders...</span>
            </h4>
            <div v-if="!shortOrdersLoading && !shortAllergyOptions.length" class="grey--text">
              No orders need an allergy sub from this component.
            </div>
            <v-tabs v-if="shortAllergyOptions.length > 1" v-model="shortAllergyTab" show-arrows class="short-allergy-tabs">
              <v-tab v-for="group of shortGroups" v-bind:key="group.key">
                {{ group.name }}
                <v-chip v-if="getGroupTotal(group)" x-small color="primary" class="ml-2">{{ getGroupTotal(group) }}</v-chip>
              </v-tab>
            </v-tabs>
          </div>
          <div class="short-group">
            <div v-if="activeShortGroup.allergies" class="short-group-header">
              <strong>{{ activeShortGroup.name }}</strong>
              <span v-if="activeShortGroup.ordered" class="grey--text ml-2">
                {{ formatOrdered(activeShortGroup.ordered) }} ordered
              </span>
            </div>
            <v-row>
              <v-col v-for="size of getGroupSizes(activeShortGroup)" v-bind:key="`${activeShortGroup.key}-${size}`"
                     cols="12" class="short-size">
                <div class="short-size-label text-capitalize">
                  {{ size }}: missing
                  <span class="short-size-value">{{ getShortCount(activeShortGroup.key, size) }}</span>
                  of {{ getShortOrdered(activeShortGroup, size) }}
                  ({{ formatWeightWithUnits(shortComponent.source.portionSizes[size]) }} each)
                </div>
                <div class="short-number-buttons">
                  <v-btn
                    v-for="n of getShortQuickPicks(activeShortGroup.key, size)"
                    v-bind:key="n"
                    :color="getShortCount(activeShortGroup.key, size) === n ? 'primary' : ''"
                    :outlined="getShortCount(activeShortGroup.key, size) !== n"
                    :disabled="isShortTaskNonEditable(shortComponent)"
                    @click="setShort(activeShortGroup.key, size, n)"
                    class="short-number-button"
                  >
                    {{ n }}
                  </v-btn>
                </div>
                <div v-if="getShortOrdered(activeShortGroup, size) > maxShortQuickPick" class="short-stepper">
                  <v-btn
                    large fab outlined
                    :disabled="isShortTaskNonEditable(shortComponent) || getShortCount(activeShortGroup.key, size) <= 0"
                    @click="setShort(activeShortGroup.key, size, getShortCount(activeShortGroup.key, size) - 1)"
                  >
                    <v-icon>mdi-minus</v-icon>
                  </v-btn>
                  <v-text-field
                    class="short-stepper-input"
                    :value="getShortCount(activeShortGroup.key, size)"
                    @input="setShort(activeShortGroup.key, size, $event)"
                    type="number"
                    min="0"
                    :max="getShortMax(activeShortGroup.key, size)"
                    outlined
                    hide-details
                    :disabled="isShortTaskNonEditable(shortComponent)"
                  />
                  <v-btn
                    large fab outlined
                    :disabled="isShortTaskNonEditable(shortComponent) || getShortCount(activeShortGroup.key, size) >= getShortMax(activeShortGroup.key, size)"
                    @click="setShort(activeShortGroup.key, size, getShortCount(activeShortGroup.key, size) + 1)"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </div>
          <v-row>
            <v-col class="text-center"><h3>Total Amount Needed {{ formatWeightWithUnits(totalShortAmount) }}</h3>
            </v-col>
          </v-row>
          <div v-if="shortComponent.source.line === 'S' && !showShortNotes" class="text-center short-notes">
            <v-btn outlined small :disabled="isShortTaskNonEditable(shortComponent)" @click="showShortNotes = true">
              <v-icon left small>mdi-note-plus-outline</v-icon>
              Add notes
            </v-btn>
          </div>
          <v-row v-if="shortComponent.source.line === 'S' && showShortNotes" dense class="short-notes">
            <v-col cols="12">
              <v-text-field
                v-model="shortNote"
                outlined dense
                label="Short note (optional, added to the task title)"
                :maxlength="shortNoteMaxLength"
                :counter="shortNoteMaxLength"
                :disabled="isShortTaskNonEditable(shortComponent)"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="shortDetails"
                outlined dense auto-grow rows="2"
                label="Details (optional, added to the task description)"
                :disabled="isShortTaskNonEditable(shortComponent)"
              />
            </v-col>
          </v-row>
          <v-alert v-if="isShortTaskNonEditable(shortComponent)" type="error" dense class="mt-3">
            This short task has been {{ getShortTaskFor(shortComponent).complete ? 'completed' : (getShortTaskFor(shortComponent).isPaused ? 'paused' : 'started') }}
            by the kitchen and can no longer be modified.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="enterShort" outlined @click="shortComponent=null;enterShort=false">Cancel</v-btn>
          <v-spacer/>
          <v-btn
            :disabled="!shortComponent || isShortTaskNonEditable(shortComponent) || !hasShortAmount"
            v-if="enterShort"
            outlined
            @click="submitShort">
            {{ getShortTaskFor(shortComponent) ? 'Update Short' : 'Submit' }}
          </v-btn>
        </v-card-actions>
      </v-card>

    </v-dialog>
    <v-dialog v-model="showKitchenDialog" max-width="700">
      <v-card v-if="kitchenDialogMeal">
        <v-card-title>
          Kitchen Status
          <v-spacer/>
          <v-btn icon @click="showKitchenDialog=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="text-capitalize">{{ kitchenDialogMeal.name }}</v-card-subtitle>
        <v-card-text>
          <div v-for="row of getKitchenRows(kitchenDialogMeal.id)" v-bind:key="row.componentId" class="meal-card-row">
            <span><strong>{{ row.batch }}</strong> {{ row.name }}</span>
            <span class="ml-auto text-no-wrap">
              <v-chip v-for="line of ['M','AL','S'].filter(l => l in row.lines)" v-bind:key="line"
                      x-small dark :color="row.lines[line] ? 'green' : 'orange'" class="ml-1">
                {{ {M: 'Main', AL: 'Allergy', S: 'Sub'}[line] }} {{ row.lines[line] ? 'ready' : 'waiting' }}
              </v-chip>
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showShortsDialog" max-width="700">
      <v-card v-if="shortsDialogMeal">
        <v-card-title>
          Short Requests
          <v-spacer/>
          <v-btn icon @click="showShortsDialog=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="text-capitalize">{{ shortsDialogMeal.name }}</v-card-subtitle>
        <v-card-text>
          <div v-for="t of getMealShorts(shortsDialogMeal.id)" v-bind:key="t.id" class="short-request">
            <div class="meal-card-row">
              <span class="meal-card-label">
                {{ formatWeightWithUnits(t.source.totalAmountMain || t.source.totalAmountAllergy || t.source.totalAmountSub) }}
              </span>
              <span>
                <strong>{{ t.source.batch }}</strong>
                {{ t.title }}
              </span>
              <v-chip x-small :color="getShortStatusColor(t)" dark class="ml-2">{{ getShortStatus(t) }}</v-chip>
              <span v-if="getShortAge(t)" class="ml-2">{{ getShortAge(t) }}</span>
              <span v-if="getTeamMembers(t)" class="ml-2">by {{ getTeamMembers(t).join(', ') }}</span>
              <v-btn x-small outlined color="primary" class="ml-2" @click="editShort(t)">
                <v-icon left x-small>mdi-pencil</v-icon>
                Edit
              </v-btn>
            </div>
            <!-- the description carries the per-allergy counts and any details -->
            <div v-if="t.description" class="short-request-description">{{ t.description }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-container v-if="loading" fluid class="justify-center text-center fill-height">
      <v-progress-circular indeterminate/>
    </v-container>
    <!--    <v-container v-if="!hasDates" fluid class="justify-center text-center fill-height flex-column">-->
    <!--      <v-alert type="info" @click="showDatePicker=true">-->
    <!--        Please select production dates-->
    <!--      </v-alert>-->
    <!--      <ProductionSelector-->
    <!--        :dates.sync="dates"-->
    <!--        :production.sync="production"-->
    <!--      />-->
    <!--    </v-container>-->
    <v-container
      v-if="!showTaskPanel && !loading"
      fluid
      class="align-self-start"
      style="margin-bottom: 10rem"
    >
      <v-alert v-if="isDisconnected" type="warning">
        Disconnected from Server. Tasks will not update!
      </v-alert>

      <v-toolbar flat dense style="font-size: smaller" :class="`${production}-production`">
        <v-chip :style="chipWidth" outlined small color="green">K
          <v-icon fab color="green">mdi-check</v-icon>
        </v-chip>
        Components Ready
        <v-spacer/>
        <span class="red--text font-weight-bold">A1 </span> Rack
        <v-spacer/>
        <div>
          <span class="font-weight-bold">C#4</span> Component Count
          <br/>
          <span class="font-weight-bold">G#2</span> Garnish Count
        </div>
        <v-spacer/>
        <span class="red--text font-weight-bold"><v-icon color="red">mdi-skull-crossbones</v-icon></span>
        Foodsafe Warning
        <v-spacer/>
        <v-chip :style="chipWidth" outlined small color="green">A
          <v-icon fab color="green">mdi-check</v-icon>
        </v-chip>
        Allergy Plated
        <v-spacer/>
        <v-chip :style="chipWidth" outlined small color="green">M
          <v-icon fab color="green">mdi-check</v-icon>
        </v-chip>
        Main Plated
        <v-spacer/>
        <ProductionSelector
          :dates.sync="dates"
          :production.sync="production"
          v-on:update="fetchData"
        />
        <v-btn icon @click="SET_HIDE_TOP_NAV({hideTopNav:!hideTopNav})">
          <v-icon>mdi-fullscreen</v-icon>
        </v-btn>
      </v-toolbar>
      <v-toolbar flat>
        <v-spacer/>
        <v-btn-toggle v-model="streamSelect" rounded>
          <v-btn :value="null" large>All</v-btn>
          <v-btn large :value="stream" v-for="stream of streams" v-bind:key="stream">{{ stream }}</v-btn>
        </v-btn-toggle>
        <v-spacer/>
      </v-toolbar>
      <v-row dense :class="`${production}-production`">
        <v-col v-for="date of datesWithOrders" v-bind:key="`${date}-stream`">
          <v-list dense>
            <v-list-item dense
                         v-for="(meal, i) in mealsWithOrders[date]"
                         :key="i"
                         :class="streamSelect ? 'meal-card' : ''"
                         :style="streamSelect ? `border-color: ${getMealStateColor(meal.id)}` : ''"
            >
              <v-list-item-content style="padding: 0" class="text-capitalize" @click="showMeal(meal)">
                <div :class="streamSelect ? 'meal-card-header' : ''">
                <div :style="isMealCrossedOff(meal.id) ? 'text-decoration: line-through':''">
                  <template v-if="!streamSelect && isColdWarning(meal)">
                    <v-icon color="orange">mdi-skull-crossbones</v-icon>
                  </template>
                  <template v-if="!streamSelect && isColdDanger(meal)">
                    <v-icon color="red">mdi-skull-crossbones</v-icon>
                  </template>
                  <template v-if="!streamSelect && isComplete(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="green">
                      K
                      <v-icon fab color="green">mdi-check</v-icon>
                    </v-chip>
                  </template>
                  <span v-if="getMealRack(meal.id)" class="red--text font-weight-bold">{{
                      getMealRack(meal.id)
                    }} </span>
                  <b v-if="meal.production_priority">P{{ meal.production_priority }}</b>
                  {{ mealNameShort(meal) }}
                  {{ getMealCount(meal.id) }}<span
                  v-if="getAllergyMealCount(meal.id)>0">+{{ getAllergyMealCount(meal.id) }}</span>CT
                  C#{{ getComponentCount(meal.id) }}
                  <span v-if="getGarnishCount(meal.id)">G#{{ getGarnishCount(meal.id) }}</span>
                  <template v-if="!streamSelect && isAllergyStarted(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="orange">A
                      <v-icon fab>mdi-play</v-icon>
                    </v-chip>
                  </template>
                  <template v-if="!streamSelect && isAllergyComplete(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="green">A
                      <v-icon fab>mdi-check</v-icon>
                    </v-chip>
                  </template>
                  <template v-if="!streamSelect && isStarted(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="orange">M
                      <v-icon fab>mdi-play</v-icon>
                    </v-chip>
                  </template>
                  <template v-if="!streamSelect && isPaused(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="blue">M
                      <v-icon fab>mdi-pause</v-icon>
                    </v-chip>
                  </template>
                  <template v-if="!streamSelect && isAllergyPaused(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="blue">A
                      <v-icon fab>mdi-pause</v-icon>
                    </v-chip>
                  </template>
                  <template v-if="!streamSelect && isMealPlated(meal.id)">
                    <v-chip :style="chipWidth" outlined small color="green">M
                      <v-icon fab>mdi-check</v-icon>
                    </v-chip>
                  </template>
                  <template v-if="!streamSelect && isMealShort(meal.id)">
                    <v-icon v-if="isMealShortComplete(meal.id)" color="green" fab @click.stop="openShortsDialog(meal)">
                      mdi-comment-check-outline
                    </v-icon>
                    <v-icon v-else color="pink" fab @click.stop="openShortsDialog(meal)">
                      mdi-comment-question-outline
                    </v-icon>
                  </template>
                </div>
                <div v-if="streamSelect" class="meal-card-kitchen text-none">
                  <v-chip v-if="isComplete(meal.id)" small color="green" dark @click.stop="openKitchenDialog(meal)">
                    <v-icon left small>mdi-check</v-icon>
                    Ready to Plate
                  </v-chip>
                  <v-chip v-else small color="orange" dark @click.stop="openKitchenDialog(meal)">
                    <v-icon left small>mdi-clock-outline</v-icon>
                    Waiting for Kitchen
                  </v-chip>
                  <v-chip v-if="isMealShort(meal.id) && !isMealShortComplete(meal.id)" small color="pink" dark class="ml-2"
                          @click.stop="openShortsDialog(meal)">
                    <v-icon left small>mdi-comment-question-outline</v-icon>
                    Waiting for Short
                  </v-chip>
                </div>
                </div>
                <div v-if="streamSelect" class="meal-card-name"
                     :style="isMealCrossedOff(meal.id) ? 'text-decoration: line-through':''">
                  <v-icon v-if="isColdWarning(meal)" color="orange">mdi-skull-crossbones</v-icon>
                  <v-icon v-if="isColdDanger(meal)" color="red">mdi-skull-crossbones</v-icon>
                  {{ meal.name }}
                </div>
                <template v-if="streamSelect">
                  <v-row dense class="meal-card-details text-none">
                    <v-col v-for="line of getPlatingLines(meal.id)" v-bind:key="`${meal.id}-${line.key}`"
                           class="meal-card-line" :style="`border-left-color: ${line.statusColor}`">
                      <div class="meal-card-row meal-card-heading">
                        <span>{{ line.label }} Plating ({{ line.count }}CT)</span>
                        <span>
                          <v-chip v-for="rack of line.racks" v-bind:key="rack" x-small outlined class="ml-1">
                            {{ rack }}
                          </v-chip>
                        </span>
                      </div>
                      <div class="meal-card-row">
                        <span class="meal-card-label">Status</span>
                        <v-chip x-small :color="line.statusColor" dark>{{ line.status }}</v-chip>
                      </div>
                      <div class="meal-card-row" v-if="line.workers.length">
                        <span class="meal-card-label">Working</span>
                        <span class="font-weight-bold">{{ line.workers.join(', ') }}</span>
                        <span v-if="line.status === 'In Progress' && getElapsedTime(line.task)"
                              class="ml-1" :class="getElapsedColorClass(line)">
                          ({{ formatDurationNoSeconds(getElapsedTime(line.task)) }})
                        </span>
                      </div>
                      <div class="meal-card-row" v-if="line.task.complete && getElapsedTime(line.task)">
                        <span class="meal-card-label">Actual</span>
                        <span class="font-weight-bold">{{ formatDuration(getElapsedTime(line.task)) }}</span>
                      </div>
                      <div class="meal-card-row">
                        <span class="meal-card-label">Estimate</span>
                        <span>{{ formatDuration(getPlatingTimeEstimate(line.count)) }}</span>
                      </div>
                      <div class="meal-card-row" v-if="line.task.note">
                        <span class="meal-card-label">Note</span>
                        <span>{{ line.task.note }}</span>
                      </div>
                    </v-col>
                  </v-row>
                  <div v-if="isMealShort(meal.id)" class="meal-card-details meal-card-shorts text-none">
                    <div class="meal-card-row meal-card-heading"><span>Short Requests</span></div>
                    <div v-for="t of getMealShorts(meal.id)" v-bind:key="t.id" class="meal-card-row">
                      <span class="meal-card-label">
                        {{ formatWeightWithUnits(t.source.totalAmountMain || t.source.totalAmountAllergy || t.source.totalAmountSub) }}
                      </span>
                      <span>
                        <strong>{{ t.source.batch }}</strong>
                        {{ t.source.line === 'S' ? 'SUB for ' : t.source.line }}
                        {{ t.source.component.name }}
                      </span>
                      <v-chip x-small :color="getShortStatusColor(t)" dark class="ml-2">{{ getShortStatus(t) }}</v-chip>
                      <span v-if="getShortAge(t)" class="ml-2">{{ getShortAge(t) }}</span>
                      <span v-if="getTeamMembers(t)" class="ml-2">by {{ getTeamMembers(t).join(', ') }}</span>
                    </div>
                  </div>
                </template>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
      <v-alert v-if="!hasDates" type="info" dense outlined class="mt-4">
        Choose a production above to see the plating tasks.
      </v-alert>
      <v-alert v-else-if="!streamSelect" type="info" dense outlined class="mt-4">
        Choose a diet above to show larger cards with more information about each plating task.
      </v-alert>
    </v-container>
    <v-footer class="" fixed>
      <v-container fluid class="mb-0 pa-0">
        <TeamMemberTaskButtons :task-map="platingTaskMap" show-short v-on:short="enterShortForTask($event)"/>
        <v-row dense v-if="overTimeTasks.length>0">
          <v-col class="text-center">
            <v-chip
              v-for="(ot,index) of overTimeTasks" v-bind:key="index"
              :color="isLateClass(now)?'red':'orange'"
              @click="showMeal(ot.meal)"
              class="mr-1 mb-1"
              outlined
              :small="overTimeTasks.length>4 && overTimeTasks.length<=6"
              :x-small="overTimeTasks.length>6"
            >
              {{ ot.team_members.join(', ') }} {{ formatMinutes(ot.lateBy) }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col class="text-left">
            {{ formatTime(now) }}
          </v-col>
          <v-col>

            Total Time Est. {{ formatDuration(totalPlatingTime) }} (1 person)
          </v-col>
          <v-col class="text-right">
            <template v-if="streamSelect">
              <v-chip class="mr-2" color="red" outlined>M {{ countSelectedMainCompleted }}/{{
                  countSelectedMain
                }}
              </v-chip>
              <v-chip class="mr-2" color="red" outlined>A {{ countSelectedAllergyCompleted }}/{{
                  countSelectedAllergy
                }}
              </v-chip>
            </template>
            <v-chip class="mr-2" color="" outlined>M {{ countMainCompleted }}/{{ countMain }}</v-chip>
            <v-chip color="" outlined>A {{ countAllergyCompleted }}/{{ countAllergy }}</v-chip>
          </v-col>

          <!--          <v-col class="text-right">-->
          <!--            Meal Count {{ totalMealCount }}-->
          <!--          </v-col>-->
        </v-row>
      </v-container>
    </v-footer>


  </v-container>
</template>


<script>
import urlState from "@/router/urlState";
import {mapActions, mapGetters, mapMutations} from "vuex";
import moment from "moment/moment";
import {
  blankAsZero,
  dateFormatted,
  formatWeightWithUnits,
  getDatesInRange,
  getDateString,
  getPlatingImageUrl,
  getPlatingTimeEstimate,
  getProductionDays,
  sortBySize
} from "@/store/utils";
import api from "@/api";
import {filterSelectedIngredients} from "@/store/utils";
import Plating from "@/components/Plating";
import TaskPanel from "@/components/tasks/TaskPanel.vue";
import ProductionSelector from "@/components/ProductionSelector.vue";
import TeamMemberTaskButtons from "@/components/TeamMemberTaskButtons.vue";

// diabetes meals are plated together with the low carb meal on the same date
// and time of day (see combineDiabetesIntoLocarb in fed-nutrition-api)
const LOCARB_DIET = 14;
const DIABETES_DIET = 17;

// separates the allergies of a combined sub short, e.g. "pork + garlic"
const SHORT_ALLERGY_JOIN = ' + ';

export default {
  name: "PlatingTasks",
  components: {Plating, ProductionSelector, TaskPanel, TeamMemberTaskButtons},
  mixins: [urlState],
  data() {
    return {
      team: 'plating',
      dates: [],
      search: null,
      teamsSelected: null,
      showDatePicker: null,
      loading: null,
      datePickerDate: null,
      showGarnish: null,
      onlyGarnish: null,
      teams: ['allergy', 'main'],
      plateTaskFilter: [],
      meals: {},
      componentTaskMap: {},
      auditTaskMap: {},
      showTaskPanel: null,
      platingTaskMap: {},
      taskMap: null,  // this just points to platingTaskMap
      platingTaskMealMap: {},
      allergyPlatingTaskMealMap: {},
      complete: {},
      selectedMeal: null,
      showMealDialog: null,
      showPlatingDetail: false,
      platingDetailRestrictions: false,
      platingDetailMeal: null,
      team_members: null,
      hideTopNav: true,
      taskConfirmStart: null,
      taskConfirmStop: null,
      now: moment(),
      timerIntervals: {},
      timeElapsed: {},
      timeStarted: {},
      timeStopped: {},
      chipWidth: 'padding:5px; width: 40px',
      isDisconnected: false,
      enterShort: null,
      shortComponent: null,
      shortLine: null, // 'main' | 'allergy' | null (both) - which line the short picker offers
      maxShortQuickPick: 10,
      shortNoteMaxLength: 30,

      short: {}, // {size: count} when no allergy is picked
      shortAllergyTab: 0, // which allergy combination's amounts are showing
      shortAllergyCounts: {}, // {groupKey: {size: count}}, groupKey = allergy names joined by ' + '
      shortOrders: null, // orders for the sub component's meal, to offer the allergy combinations they need
      shortOrdersLoading: false,
      showShortNotes: false, // note fields stay hidden until asked for
      shortNote: '', // optional short note, appended to the task title (sub shorts)
      shortDetails: '', // optional longer note, appended to the task description (sub shorts)
      showGreyAllergies: false,
      showShortsDialog: false,
      shortsDialogMeal: null,
      showKitchenDialog: false,
      kitchenDialogMeal: null,
      streamSelect: null,
      production: null
    }
  },
  watch: {
    showMealDialog(open) {
      if (!open) {
        // leave short entry mode when the meal dialog closes
        this.enterShort = false;
        this.shortComponent = null;
        this.shortLine = null;
      }
    },
    isDisconnected(val, old) {
      if (!val && old) {
        console.log('reconnected, refetch tasks');
        this.fetchData();
      } else {
        console.log('isDisconncted skip, ', {val, old});
      }
    },
    // every allergy combination has its own counts, starting at zero
    shortAllergyOptions(options) {
      this.shortAllergyTab = 0;
      for (const option of options) {
        if (!this.shortAllergyCounts[option.key]) {
          this.$set(this.shortAllergyCounts, option.key, this.zeroCounts());
        }
      }
    },
    shortComponent(v) {
      console.log('short component', v);
      if (v && !v.source.portionSizes) {
        alert('no portion sizes found for ' + v.description);
      }

      this.shortOrders = null;
      if (v && v.source.line === 'S') {
        this.loadShortOrders(v);
      }
      if (v) {
        // Check if there's an existing short task for this component
        const existingShort = this.getShortTaskFor(v);

        if (existingShort && !existingShort.isStarted && !existingShort.complete) {
          // Pre-fill with existing values if short exists but hasn't been started or completed
          const {shortAllergyCounts = {}, shortNote = '', shortDetails = ''} = existingShort.source;
          this.shortAllergyCounts = JSON.parse(JSON.stringify(shortAllergyCounts));
          this.short = Object.keys(shortAllergyCounts).length ? this.zeroCounts() : {...existingShort.source.count};
          this.shortNote = shortNote;
          this.shortDetails = shortDetails;
          this.showShortNotes = !!(shortNote || shortDetails);
          console.log('Pre-filling short with existing values', this.short, this.shortAllergyCounts);
        } else {
          this.resetShortEntry();
        }
      } else {
        this.resetShortEntry();
      }
    }
  },
  mounted() {
    this.fetchRestrictions();
    this.taskMap = this.platingTaskMap;
    this.SET_HIDE_TOP_NAV({hideTopNav: this.hideTopNav});
    this.syncToUrl({
      param: 'plateTaskFilter', urlParam: 'plating_filter', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'taskFilter', urlParam: 'status', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'production', urlParam: 'production', initFromRoute: true,
      // parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'streamSelect', urlParam: 'streams', initFromRoute: true,
      // parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'search', urlParam: 'search', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    if (this.platingMode === 'Allergy') {
      if (this.plateTaskFilter.length === 0) {
        this.plateTaskFilter.push('racked');
      }
    }
    this.$socket.on('task:started', (task) => {
      console.log('task started', task);
      this.updateComponentTask(task);
      this.updatePlatingTask(task);
      this.updateAuditTask(task);
    });
    this.$socket.on('task:stopped', (task) => {
      console.log('task stopped', task);
      this.updateComponentTask(task);
      this.updatePlatingTask(task);
      this.updateAuditTask(task);
      this.stopTimer(task);
    });
    this.$socket.on('task:updated', (task) => {
      console.log('task updated', task);
      this.updateComponentTask(task);
      this.updatePlatingTask(task);
      this.updateAuditTask(task);
    });
    this.$socket.on('task:created', (task) => console.log('task created', task));
    this.$socket.emit('members:fetch',
      {team: this.team},
      (result) => {
        const {error, teamMembers} = result;
        if (error) {
          console.warn(`members:fetch failed: ${error}`);
        } else {
          console.log('plating members:fetch result', teamMembers);
          this.team_members = teamMembers;
        }
      }
    );
    this.$socket.on('connect', () => this.isDisconnected = false);
    this.$socket.on('disconnect', () => this.isDisconnected = true);
    this.$nextTick(() => this.fetchData());
    const intervalId = setInterval(() => {
      this.now = moment();
    }, 1000);
    this.$set(this.timerIntervals, 'main-clock', intervalId);
  },
  destroyed() {
    // for all events
    console.log('removing listeners/intervals');
    this.$socket.removeAllListeners();
    Object.values(this.timerIntervals)
      .forEach(timer => clearInterval(timer));
  },
  methods: {
    isLateClass(now) {
      if (now.second() % 2 === 1) {
        return true;
      } else {
        return false;
      }
    },
    formatMinutes(ms) {
      if (ms && !isNaN(ms)) {
        if (ms > (1000 * 60 * 60 * 24)) {
          return (moment.duration(ms).asHours()).toFixed(1) + ' hours';
        } else {
          const m = moment.utc(ms || 0);
          if (m.hours() > 0) {
            return m.format('H[h] m[m]');
          } else if (m.minutes() > 0) {
            return m.format('m[m]');
          } else {
            return m.format('s[s]');
          }
        }
      }
    },
    formatDurationNoSeconds(ms) {
      if (!ms || isNaN(ms)) {
        return '';
      }
      if (ms > (1000 * 60 * 60 * 24)) {
        return (moment.duration(ms).asHours()).toFixed(1) + ' hours';
      }
      const m = moment.utc(ms);
      if (m.hours() > 0) {
        return m.format('HH[h] mm[m]');
      }
      if (m.minutes() > 0) {
        return m.format('mm[m]');
      }
      return m.format('ss[s]');
    },
    getElapsedColorClass(line) {
      const elapsed = this.getElapsedTime(line.task);
      const estimate = getPlatingTimeEstimate(line.count);
      if (!elapsed || !estimate) {
        return '';
      }
      if (elapsed >= estimate * 2) {
        return 'red--text font-weight-bold';
      }
      if (elapsed > estimate) {
        return 'orange--text font-weight-bold';
      }
      return '';
    },
    formatDuration(ms) {
      if (ms && !isNaN(ms)) {
        if (ms > (1000 * 60 * 60 * 24)) {
          return (moment.duration(ms).asHours()).toFixed(1) + ' hours';
        } else {
          const m = moment.utc(ms || 0);
          if (m.hours() > 0) {
            return m.format('HH[h] mm[m] ss[s]');
          } else if (m.minutes() > 0) {
            return m.format('mm[m] ss[s]');
          } else {
            return m.format('ss[s]');
          }
        }
      }
    },
    ...mapActions(['fetchDiets', 'fetchOrdersByDate', 'fetchMeal', 'fetchMeals', 'fetchMealsById', 'fetchRestrictions', 'fetchMealIngredients']),
    async showPlatingDetailFor(restrictions = false) {
      this.platingDetailRestrictions = restrictions;
      this.platingDetailMeal = null;
      this.showPlatingDetail = true;
      // the meal rows in the lists do not carry the photos
      this.platingDetailMeal = await this.fetchMeal(this.selectedMeal.id);
    },
    allergyNames(restrictionIds) {
      return (restrictionIds || []).map(this.getRestrictionText);
    },
    ...mapMutations(['SET_HIDE_TOP_NAV']),
    getDatesInRange,
    trunc(s, len) {
      return s.slice(0, len);
    },
    dateFormatted,
    closeDatePicker() {
      this.showDatePicker = false;
      this.dates = getProductionDays(this.datePickerDate);
      console.log('setting dates', this.dates);
      this.clearData();
      return this.fetchData();
    },
    clearData() {
      this.meals = {};
    },
    async fetchData() {
      this.error = this.post = null

      if (!(this.dates && this.dates.length > 0 && this.production)) {
        return;
      }
      this.loading = true;

      this.meals = {};
      this.platingTaskMap = {};
      this.taskMap = this.platingTaskMap; // keep the alias pointing at the new map or timers never start
      this.componentTaskMap = {};
      this.auditTaskMap = {};
      this.platingTaskMealMap = {};
      this.allergyPlatingTaskMealMap = {};
      this.dates.map(date => this.$set(this.meals, date, []));
      await Promise
        .all([
          this.fetchDiets(),
          // ...this.dates.map(date => this
          // .fetchOrdersByDate({date})
          // .then(orders => this.$set(this.orders, date, orders))),
          ...this.dates.map(date => this.fetchMeals(date).then(meals => this.$set(this.meals, date, meals.slice()))),
          this.fetchComponentTasks(),
          this.fetchAuditorTasks(),
          this.fetchPlatingTasks(),
        ])
        .finally(() => this.loading = false);

      // get CC meals
      const mealIds = [];
      for (const task of this.tasks) {
        if (task.source.dietName === 'Chefs Choice') {
          mealIds.push(task.source.mealId)
        }
      }
      const ccMeals = await this.fetchMealsById(mealIds);
      this.meals[this.dates[0]].push(...ccMeals);
      this.$nextTick(() => {
        if (this.streamSelect) {
          if (!this.streams.includes(this.streamSelect)) {
            this.streamSelect = null;
          }
        }
      })
    },
    fetchPlatingTasks() {
      const dates = this.dates;
      if (dates && dates.length > 0)
        return new Promise(resolve => {
          this.$socket.emit('tasks:fetch',
            {dates, team: this.team, productions: [this.production]},
            (result) => {
              const {error, tasks} = result;
              if (error) {
                console.warn(`fetch tasks ${dates} failed: ${error}`);
              } else {
                console.log(`tasks:fetch team=${this.team}- ${dates} result`, result);
                tasks.forEach(task => {
                  const {id} = task;
                  this.$set(this.platingTaskMap, id, {...(this.platingTaskMap[id] || {}), ...task});
                  this.updatePlatingTask(task);
                });
              }
              resolve();
            }
          );
        })
    },
    async fetchAuditorTasks() {
      const dates = this.dates;
      this.incomplete = {};
      this.auditTaskMap = {};
      if (dates && dates.length > 0)
        return new Promise(resolve => {
          const team = 'audit';
          this.$socket.emit('tasks:fetch',
            {dates, team, productions: [this.production]},
            (result) => {
              const {error, tasks} = result;
              if (error) {
                alert(`fetch tasks ${dates} failed: ${error}`);
              } else {
                console.log(`tasks:fetch team=${team} - ${dates} result`, result);
                tasks.forEach(task => {
                  const {id} = task;
                  this.$set(this.auditTaskMap, id, {...(this.auditTaskMap[id] || {}), ...task});
                  this.updateAuditTask(task);
                });
              }
              resolve();
            }
          );
        });
    },
    async fetchComponentTasks() {
      const dates = this.dates;
      this.incomplete = {};
      this.componentTaskMap = {};
      if (dates && dates.length > 0)
        return new Promise(resolve => {
          const team = 'components';
          this.$socket.emit('tasks:fetch',
            {dates, team, productions: [this.production]},
            (result) => {
              const {error, tasks} = result;
              if (error) {
                alert(`fetch tasks ${dates} failed: ${error}`);
              } else {
                console.log(`tasks:fetch team=${team} - ${dates} result`, result);
                tasks.forEach(task => {
                  const {id} = task;
                  this.$set(this.componentTaskMap, id, {...(this.componentTaskMap[id] || {}), ...task});
                  this.updateComponentTask(task);
                });
              }
              resolve();
            }
          );
        });
    },
    sortMeals(meals) {
      meals.sort((m1, m2) => {
        const meal1Plated = this.isMealCrossedOff(m1.id);
        const meal2Plated = this.isMealCrossedOff(m2.id);
        if (meal1Plated && !meal2Plated) {
          return 1;
        }
        if (!meal1Plated && meal2Plated) {
          return -1;
        }

        const m1Racked = this.getMealRack(m1.id);
        const m2Racked = this.getMealRack(m2.id);
        if (m1Racked && !m2Racked) {
          return -1;
        }
        if (m2Racked && !m1Racked) {
          return 1;
        }


        const diff = this.getMealCount(m2.id) - this.getMealCount(m1.id);
        if (diff !== 0) {
          return diff;
        } else {
          return m1.name.localeCompare(m2.name);
        }
      });
      return meals;
    },
    getMealRack(mealId) {
      return this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].source.rack;
    },
    getMealCount(mealId) {
      const count = (this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].source.mealCount) || 0;
      return count - this.getAllergyMealCount(mealId);
    },
    getAllergyLines(mealId) {
      return [...new Set(this.getAllergyLineAuditTasks(mealId).map(t => t.source.line))];
    },
    getAllergyMealCount(mealId) {
      return (this.allergyPlatingTaskMealMap[mealId] && this.allergyPlatingTaskMealMap[mealId].source.mealCount) || 0;
    },
    getComponentCount(mealId) {
      return (this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].source.componentNotGarnishCount) || 0;
    },
    getGarnishCount(mealId) {
      return (this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].source.garnishCount) || 0;
    },
    isMealPlated(mealId) {
      return this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].complete;
    },
    hasAllergyDemand(mealId) {
      return this.getAllergyMealCount(mealId) > 0;
    },
    isMealCrossedOff(mealId) {
      if (!this.isMealPlated(mealId)) {
        return false;
      }
      return !this.hasAllergyDemand(mealId) || this.isAllergyComplete(mealId);
    },
    isComplete(mealId) {
      if (this.complete[mealId]) {
        return Object.values(this.complete[mealId]).every(t => t);
      } else {
        return false;
      }
    },
    updatePlatingTask(task) {
      const {id} = task;
      if (!this.platingTaskMap[id]) {
        // console.log('skipping, not in map', task);
        return;
      }

      const updatedTask = {...this.platingTaskMap[id], ...task};
      this.$set(this.platingTaskMap, id, updatedTask);
      task = updatedTask;

      if (task.source && task.source.mealId) {
        // some tasks may not have a meal
        const mealId = task.source.mealId;
        if (task.source.allergy) {
          this.$set(this.allergyPlatingTaskMealMap, mealId, task);
        } else {
          this.$set(this.platingTaskMealMap, mealId, task);
        }
      }
      const intervalId = setInterval(() => {
        this.updateElapsed(id);
      }, 1000);
      this.$set(this.timerIntervals, id, intervalId);
      this.$set(this.timeElapsed, task.id, task.elapsed);
      const executing = task.execution.find(e => !e.stop)
      if (executing) {
        // console.log('starting timer for task', {task, executing});
        this.startedTimer({id: task.id, start: executing.start});
      }
      // console.log(`updating ${mealId} ac=${task.source.allergyComplete} r=${task.source.rack}`)
    },
    startedTimer(task) {
      let {id, start} = task;
      // console.log('start timer', task);
      if (!this.taskMap[id]) {
        console.log('ignoring task, not in task map', task);
        return;
      }

      const updatedTask = {...this.taskMap[id], ...task};
      this.$set(this.taskMap, id, updatedTask);
      task = updatedTask;

      if (this.isStarted(id)) {
        // console.log('already started, nothing to do', {id});
      } else {
        start = Date.parse(start);
        if (!start) {
          alert('no start time!');
          return;
        }
        this.timeStarted[id] = start;
        this.timeStopped[id] = false;
        if (this.timerIntervals[id]) {
          clearInterval(this.timerIntervals[id]);
          this.timerIntervals[id] = false;
        }
        const intervalId = setInterval(() => {
          this.updateElapsed(id);
        }, 1000);
        this.$set(this.timerIntervals, id, intervalId);
      }
    },
    stopTimer(task) {
      const {id} = task;
      console.log('stopping', task);
      if (!this.taskMap[id]) {
        console.log('ignoring task, not in task map', task);
        return;
      }
      const updatedTask = {...this.taskMap[id], ...task};
      this.$set(this.taskMap, id, updatedTask);
      task = updatedTask;

      if (this.timeStopped[id]) {
        console.log('already stopped, nothing to do', {id});
      } else {
        this.timeStopped[id] = Date.now();
        // this.teamAssignments[id] = [];
        if (this.timerIntervals[id]) {
          clearInterval(this.timerIntervals[id]);
          this.timerIntervals[id] = false;
        }
        this.updateElapsed(id);
      }
    },
    updateElapsed(id) {
      let elapsed = this.timeElapsed[id];
      let started = this.timeStarted[id];
      let stopped = this.timeStopped[id];
      if (started && !stopped) {
        const now = Date.now();
        elapsed += now - started;
        // a bit hacky, but since elapsed has been updated, ensure started is updated too
        this.timeStarted[id] = now;
      }
      // console.log('update elapsed', {id, elapsed, started, stopped});
      this.$set(this.timeElapsed, id, elapsed);
    },
    updateComponentTask(task) {
      const {id} = task;
      if (!this.componentTaskMap[id]) {
        // console.log('skipping, not in map', task);
        return;
      }

      const updatedTask = {...this.componentTaskMap[id], ...task};
      this.$set(this.componentTaskMap, id, updatedTask);
      task = updatedTask;

      // REMAINDER NO LONGER USED - as complete is based on audit tasks
      // console.log('updating task', task);
      // const source = task.source;
      // if (!source) {
      //   // console.log('no source, skipping', task);
      //   return;
      // }
      // const componentId = source.component && source.component.id;
      // const componentName = source.component && source.component.name;
      // if (componentName.match(/garnish/i)) {
      //   // console.log('skipping garnish', componentName);
      //   return;
      // }
      // const complete = task.complete;
      // if (source && source.meals) {
      //   for (const meal of source.meals) {
      //     const {meal_id} = meal;
      //     this.$set(this.complete, meal_id, this.complete[meal_id] || {});
      //     this.$set(this.complete[meal_id], componentId, complete);
      //   }
      // }
    },
    updateAuditTask(task) {
      const {id} = task;
      if (!this.auditTaskMap[id]) {
        return;
      }
      // console.log('updating task', task);
      const updatedTask = {...this.auditTaskMap[id], ...task};
      this.$set(this.auditTaskMap, id, updatedTask);
      task = updatedTask;

      const {source, description} = task;
      if (/garnish/i.test(description)) {
        // console.log('skipping garnish',description);
        return;
      }

      if (!source) {
        // console.log('no source, skipping', task);
        return;
      }
      if (source.line === 'M') {
        const totalAmountMeasured = source.measurements.reduce((sum, m) => sum + m.net_weight, 0);
        let complete;

        if (source.skipped) {
          complete = true;
        } else if (totalAmountMeasured >= source.totalAmount) {
          complete = true;
        } else {
          //check counts
          const {count} = source;
          const measurement = source.measurements[source.measurements.length - 1];
          complete = count && measurement && measurement.counts && Object.keys(count).every((size) => count[size] === measurement.counts[size]);
          //
          // if (source.mealId == '30592') {
          //   console.log('adding complete status', task.title, description, complete, count, measurement, source);
          // }
        }

        this.$set(this.complete, source.mealId, this.complete[source.mealId] || {});
        // this.$set(this.complete[source.mealId], `${(source.componentId)}-${source.line}`, complete);
        this.$set(this.complete[source.mealId], source.componentId, complete);
      }
    },
    mealNameShort(meal) {
      if (meal.diet === 23) {
        return meal.name;
      }

      const dietName = this.getDietName(meal.diet);

      function getInitials(str) {
        return str.split(' ').map(word => word[0]).join('');
      }

      let initials = getInitials(dietName).toUpperCase();
      if (initials === 'P') {
        initials = 'PB';
      }
      return (`${initials}-` +
        `${this.trunc(this.getTimeOfDay(meal.tod), 1)}-` +
        `${dateFormatted(meal.date, {formatString: 'ddd'})}`)
        .toUpperCase();

    },
    formatWeightWithUnits,
    allowedDates(val) {
      const notAllowed = {
        0: true,
        // 2: true,
        // 4: true
      };
      return !notAllowed[moment(val).day()];
    },
    saveRack(confirmTask, allergyTask) {
      console.log('save rack', confirmTask)
      api.put(`v2/meal/${confirmTask.source.mealId}`, {meal: {'plating_rack': confirmTask.source.rack}});
      // this lets other clients know to update the rack
      this.$socket.emit('task:update', confirmTask);
      // a bit janky, allergy rack should be updated too
      allergyTask.source.rack = confirmTask.source.rack;
      this.$socket.emit('task:update', allergyTask);
    },
    isStarted(mealId) {
      const task = this.platingTaskMealMap[mealId];
      return task && !!task.execution.find(e => !e.stop);
    },
    isAllergyStarted(mealId) {
      const task = this.allergyPlatingTaskMealMap[mealId];
      return task && task.execution && !!task.execution.find(e => !e.stop);
    },
    isPaused(mealId) {
      const task = this.platingTaskMealMap[mealId];
      return task && !this.isMealPlated(mealId) && task.execution.length > 0 && !task.execution.find(e => !e.stop);
    },
    isAllergyPaused(mealId) {
      const task = this.allergyPlatingTaskMealMap[mealId];
      return task && !this.isAllergyComplete(mealId) && task.execution.length > 0 && !task.execution.find(e => !e.stop);
    },
    isAllergyComplete(mealId) {
      return !this.allergyPlatingTaskMealMap[mealId]
        ? false // note that there may not be an allergy meal task if there are no allergies
        : this.allergyPlatingTaskMealMap[mealId].complete
    },
    formatTime(t) {
      return t ? moment(t).format('HH:mm') : '';
    },
    getElapsedTime(task) {
      return this.timeElapsed[task.id];
    },
    getMealElapsedTime(meal) {
      const mealTask = this.platingTaskMealMap[meal.id];
      const allergyTask = this.allergyPlatingTaskMealMap[meal.id];
      return (mealTask ? this.getElapsedTime(mealTask) : 0) +
        (allergyTask ? this.getElapsedTime(allergyTask) : 0);
    },
    isColdWarning(meal) {
      return !this.isColdDanger(meal) && this.getMealElapsedTime(meal) > (30 * 60 * 1000);
    },
    isColdDanger(meal) {
      const mealElapsedTime = this.getMealElapsedTime(meal);
      return mealElapsedTime > (40 * 60 * 1000);
    },
    getSafeTimeLeft(meal) {
      const mealElapsedTime = this.getMealElapsedTime(meal);
      return Math.max((40 * 60 * 1000) - mealElapsedTime, 0);
    },
    getExceededSafeTime(meal) {
      const mealElapsedTime = this.getMealElapsedTime(meal);
      return Math.max(mealElapsedTime - (40 * 60 * 1000), 0);
    },
    asMilli(s) {
      return s ? moment(s).valueOf() : 0;
    },

    getCurrentTeamMembers(task) {
      const execution = task.execution[task.execution.length - 1];
      // console.log('execution',execution);
      return execution.team_members.map(m => m.name);
    },
    showMeal(meal) {
      this.selectedMeal = meal;
      this.showMealDialog = true;
    },
    enterShortForTask(platingTask) {
      const meal = this.mealMap[platingTask.source.mealId];
      if (!meal) {
        alert(`Could not find meal for ${platingTask.title}`);
        return;
      }
      this.showMeal(meal);
      this.startShort(platingTask.source.allergy ? 'allergy' : 'main');
    },
    /**
     * Quick pick buttons 0..N for a portion size, capped so they fit on a tablet.
     */
    getShortQuickPicks(groupKey, size) {
      const top = Math.min(this.getShortMax(groupKey, size), this.maxShortQuickPick);
      return Array.from({length: top + 1}, (_, n) => n);
    },
    zeroCounts() {
      return Object.fromEntries(Object.keys(this.shortComponent.source.count).map(k => ([k, 0])));
    },
    resetShortEntry() {
      this.short = this.shortComponent ? this.zeroCounts() : {};
      this.shortAllergyTab = 0;
      this.shortAllergyCounts = {};
      this.shortNote = '';
      this.shortDetails = '';
      this.showShortNotes = false;
    },
    /**
     * Orders for the sub component's meal, so the picker can offer the allergy
     * combinations those orders need from this component.
     */
    async loadShortOrders(auditTask) {
      const {mealId} = auditTask.source;
      const meal = this.mealMap[mealId];
      const mealDate = getDateString(meal && meal.date);
      // chef's choice meals are not menued on a production day (see platingDetailDate)
      const date = this.dates.includes(mealDate) ? mealDate : this.dates[0];
      this.shortOrdersLoading = true;
      try {
        const [{data: orders}] = await Promise.all([
          api.get('v2/order/search', {params: {meal_id: mealId, date, productions: [this.production]}}),
          this.fetchMealIngredients({id: mealId})
        ]);
        if (this.shortComponent === auditTask) {
          this.shortOrders = orders;
        }
      } catch (e) {
        console.error('failed to load orders for the short picker', e);
      } finally {
        this.shortOrdersLoading = false;
      }
    },
    // portions entered for a group across all sizes, shown on its tab
    getGroupTotal(group) {
      return Object.values(this.getShortGroupCounts(group.key))
        .reduce((sum, n) => sum + blankAsZero(n), 0);
    },
    // portions ordered for a size: the group's own orders when known, else the whole component
    getShortOrdered(group, size) {
      return group.ordered ? blankAsZero(group.ordered[size]) : this.shortComponent.source.count[size];
    },
    getGroupSizes(group) {
      return this.shortSizes.filter(size => this.getShortOrdered(group, size) > 0);
    },
    formatOrdered(ordered) {
      return Object.entries(sortBySize(ordered))
        .filter(([, n]) => blankAsZero(n) > 0)
        .map(([size, n]) => `${n} ${size}`)
        .join(', ');
    },
    getShortWeight(counts) {
      const portionSizes = this.shortComponent.source.portionSizes;
      return Object.entries(counts)
        .map(([size, count]) => portionSizes[size] * blankAsZero(count))
        .reduce((sum, i) => sum + i, 0);
    },
    getShortGroupCounts(groupKey) {
      return groupKey === null ? this.short : (this.shortAllergyCounts[groupKey] || {});
    },
    getShortCount(groupKey, size) {
      return blankAsZero(this.getShortGroupCounts(groupKey)[size]);
    },
    /**
     * Most portions this group can be short by: what was ordered less what the
     * other allergy groups already claim for the size.
     */
    getShortMax(groupKey, size) {
      const group = this.shortGroups.find(g => g.key === groupKey);
      if (group && group.ordered) {
        return blankAsZero(group.ordered[size]);
      }
      const ordered = this.shortComponent.source.count[size];
      const claimedByOthers = this.shortGroups
        .filter(g => g.key !== groupKey)
        .map(g => this.getShortCount(g.key, size))
        .reduce((sum, n) => sum + n, 0);
      return Math.max(ordered - claimedByOthers, 0);
    },
    setShort(groupKey, size, value) {
      const clamped = Math.min(Math.max(blankAsZero(value), 0), this.getShortMax(groupKey, size));
      const counts = groupKey === null ? this.short : this.shortAllergyCounts[groupKey];
      this.$set(counts, size, clamped);
    },
    /**
     * Stop a plating task directly (Done or Pause) without the comment dialog.
     */
    stopTask(task, complete) {
      const {id} = task;
      console.log('task:stop', {id, complete});
      this.$socket.emit('task:stop', {id, complete}, (result) => {
        console.log('task:stop - result', result);
      });
      this.showMealDialog = false;
    },
    // allergies detected in the component - always shown (red)
    getCellDetectedAllergies(cell) {
      return cell.isSub ? this.allergyNames(cell.task.source.allergies) : [];
    },
    // allergy-line allergies not in the component - behind the toggle (grey)
    getCellOtherAllergies(cell) {
      const source = cell.task.source;
      return cell.isSub
        ? this.allergyNames(source.allergyLineAllergy)
        : this.allergyNames(source.allergies);
    },
    openKitchenDialog(meal) {
      this.kitchenDialogMeal = meal;
      this.showKitchenDialog = true;
    },
    // same readiness rule the K check uses in updateAuditTask
    isAuditTaskReady(task) {
      const {source} = task;
      if (!source) {
        return false;
      }
      if (source.skipped) {
        return true;
      }
      const measurements = source.measurements || [];
      const totalAmountMeasured = measurements.reduce((sum, m) => sum + m.net_weight, 0);
      if (totalAmountMeasured >= source.totalAmount) {
        return true;
      }
      const {count} = source;
      const measurement = measurements[measurements.length - 1];
      return !!(count && measurement && measurement.counts &&
        Object.keys(count).every(size => count[size] === measurement.counts[size]));
    },
    /** per-component kitchen readiness by line, components still waiting on the main line first */
    getKitchenRows(mealId) {
      const rows = {};
      for (const task of Object.values(this.auditTaskMap).filter(t => t.source && t.source.mealId === mealId)) {
        const {componentId, batch, line} = task.source;
        rows[componentId] = rows[componentId] || {componentId, batch, name: task.description, lines: {}};
        rows[componentId].lines[line] = this.isAuditTaskReady(task);
      }
      const key = r => `${r.lines.M === false ? 0 : 1} ${r.batch}`;
      return Object.values(rows).sort((a, b) => key(a).localeCompare(key(b)));
    },
    openShortsDialog(meal) {
      this.shortsDialogMeal = meal;
      this.showShortsDialog = true;
    },
    /** jump from a short request to its entry form in the meal dialog */
    editShort(shortTask) {
      const {shortMealId, componentId, line} = shortTask.source;
      const auditTask = Object.values(this.auditTaskMap).find(t =>
        t.source.mealId === shortMealId &&
        t.source.componentId === componentId &&
        t.source.line === line);
      if (!auditTask) {
        alert(`no audit task found for ${shortTask.source.component.name}`);
        return;
      }
      this.showShortsDialog = false;
      this.selectedMeal = this.shortsDialogMeal;
      this.showMealDialog = true;
      this.shortLine = line === 'M' ? 'main' : 'allergy';
      this.shortComponent = auditTask;
      this.enterShort = true;
    },
    startShort(line) {
      this.shortComponent = null;
      this.shortLine = line;
      this.enterShort = true;
    },
    getShortAmount(shortTask) {
      const {totalAmountMain, totalAmountAllergy, totalAmountSub} = shortTask.source;
      return formatWeightWithUnits(totalAmountMain || totalAmountAllergy || totalAmountSub);
    },
    getShortCountString(shortTask) {
      return Object.entries(sortBySize(shortTask.source.count))
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}:${v}`)
        .join(' ');
    },
    getShortAge(shortTask) {
      const {requestedAt} = shortTask.source;
      return requestedAt ? moment(requestedAt).from(this.now) : '';
    },
    /**
     * Rows for the short component picker: one cell per component on the main line,
     * allergy line rows carry the substitute for the same component as a second cell.
     */
    getShortPickerRows(mealId) {
      if (this.shortLine === 'allergy') {
        return this.getAllergyLineShortRows(mealId).map(row => ({
          key: row.componentId,
          cells: [
            {key: 'AL', task: row.allergy, isSub: false},
            {key: 'S', task: row.sub, isSub: true},
          ]
        }));
      }
      return this.getCombinedAuditTasks(mealId)
        .filter(t => t.source.line === 'M')
        .map(task => ({
          key: task.id,
          cells: [{key: 'M', task, isSub: false}]
        }));
    },
    /**
     * Allergy line audit tasks paired with the substitute task for the same component.
     */
    getAllergyLineShortRows(mealId) {
      const rows = {};
      for (const task of this.getCombinedAuditTasks(mealId).filter(t => t.source.line !== 'M')) {
        const {componentId, line, batch} = task.source;
        const isDiabetes = task.source.mealId !== mealId;
        rows[componentId] = rows[componentId] || {componentId, batch, isDiabetes, allergy: null, sub: null};
        if (line === 'S') {
          rows[componentId].sub = task;
        } else {
          rows[componentId].allergy = task;
        }
      }
      const key = (row) => `${row.isDiabetes ? 1 : 0} ${row.batch}`;
      return Object.values(rows).sort((a, b) => key(a).localeCompare(key(b)));
    },
    getPlatingTimeEstimate,
    /**
     * The meal's id plus, for a low carb meal, the diabetes meal plated
     * together with it (same date and time of day).
     */
    getCombinedMealIds(mealId) {
      for (const mealsOnDate of Object.values(this.meals)) {
        const meal = mealsOnDate.find(m => m.id === mealId);
        if (!meal) {
          continue;
        }
        if (meal.diet !== LOCARB_DIET) {
          return [mealId];
        }
        return [
          mealId,
          ...mealsOnDate
            .filter(m => m.diet === DIABETES_DIET && m.tod === meal.tod)
            .map(m => m.id),
        ];
      }
      return [mealId];
    },
    /**
     * Audit tasks to offer in the short picker: the meal's own plus those of the
     * diabetes meal plated together with it. A component used by both meals
     * keeps only the low carb task, so only diabetes-specific components are added.
     */
    getCombinedAuditTasks(mealId) {
      const mealIds = this.getCombinedMealIds(mealId);
      const tasks = Object.values(this.auditTaskMap)
        .filter(t => mealIds.includes(t.source.mealId));
      const ownComponentIds = new Set(
        tasks.filter(t => t.source.mealId === mealId).map(t => t.source.componentId)
      );
      // diabetes-specific components group at the bottom of the picker
      const key = (a) => `${a.source.mealId === mealId ? 0 : 1} ${a.source.batch} ${a.source.line}`;
      return tasks
        .filter(t => t.source.mealId === mealId || !ownComponentIds.has(t.source.componentId))
        .sort((a, b) => key(a).localeCompare(key(b)));
    },
    getAllergyLineAuditTasks(mealId) {
      // console.log('looking for allergy line audit tasks',mealId);
      const key = (a) => `${a.source.batch} ${a.source.line}`;
      return Object.values(this.auditTaskMap)
        .filter(t => t.source.mealId === mealId)
        .filter(t => t.source.line !== 'M')
        .sort((a, b) => key(a).localeCompare(key(b)));
    },
    submitShort() {
      this.$socket.emit('task:create', this.shortTask, response => {
        console.log('task:create response', response)
        this.$set(this.componentTaskMap, response.id, response);
      });
      // back to the component list, which shows the request once the server responds
      this.shortComponent = null;
    },
    isMealShort(mealId) {
      return this.getMealShorts(mealId).length > 0;
    },
    isMealShortComplete(mealId) {
      return this.getMealShorts(mealId).every(t => t.complete);
    },
    getMealShorts(mealId) {
      const mealIds = this.getCombinedMealIds(mealId);
      return this.shortComponentTasks.filter(t => mealIds.includes(t.source.shortMealId));
    },
    getTeamMembers(t) {
      const [execution] = t.execution;
      if (execution) {
        return execution.team_members.map(t => t.name);
      }
    },
    getShortStatus(shortTask) {
      // order below matters
      if (shortTask.complete)
        return 'Completed';
      if (shortTask.isPaused)
        return 'Paused';
      if (shortTask.isStarted)
        return 'Started';
      return 'Created';
    },
    getShortStatusColor(shortTask) {
      const statusMap = {
        Started: 'orange',
        Created: 'blue',
        Completed: 'green',
        Paused: 'orange',
      }
      return statusMap[this.getShortStatus(shortTask)];
    },
    getShortTaskStatus(auditTask) {
      return this.getShortStatus(this.getShortTaskFor(auditTask));
    },
    getShortTaskStatusColor(auditTask) {
      return this.getShortStatusColor(this.getShortTaskFor(auditTask));
    },
    /**
     * Main and allergy plating lines for a meal, with the status shown on the detail cards.
     */
    getPlatingLines(mealId) {
      const rack = this.getMealRack(mealId) || '';
      const lines = [];
      const mainTask = this.platingTaskMealMap[mealId];
      if (mainTask) {
        lines.push({
          key: 'M',
          label: 'Main',
          task: mainTask,
          workers: this.getCurrentWorkers(mainTask),
          count: this.getMealCount(mealId),
          racks: rack ? [`M${rack}`] : [],
          ...this.platingLineStatus(this.isMealPlated(mealId), this.isStarted(mealId), this.isPaused(mealId)),
        });
      }
      const allergyTask = this.allergyPlatingTaskMealMap[mealId];
      if (allergyTask) {
        lines.push({
          key: 'A',
          label: 'Allergy',
          task: allergyTask,
          workers: this.getCurrentWorkers(allergyTask),
          count: this.getAllergyMealCount(mealId),
          racks: rack ? this.getAllergyLines(mealId).map(line => `${line}${rack}`) : [],
          ...this.platingLineStatus(this.isAllergyComplete(mealId), this.isAllergyStarted(mealId), this.isAllergyPaused(mealId)),
        });
      }
      return lines;
    },
    getCurrentWorkers(task) {
      return task.execution
        .filter(e => !e.stop)
        .flatMap(e => e.team_members.map(m => m.name));
    },
    /**
     * Border colour for a meal card: any line in progress wins, then paused, then fully plated.
     */
    getMealStateColor(mealId) {
      const lines = this.getPlatingLines(mealId);
      if (lines.some(line => line.status === 'In Progress')) {
        return 'orange';
      }
      if (lines.some(line => line.status === 'Paused')) {
        return 'blue';
      }
      if (this.isMealCrossedOff(mealId)) {
        return 'green';
      }
      return 'grey';
    },
    platingLineStatus(complete, started, paused) {
      if (complete) {
        return {status: 'Plated', statusColor: 'green'};
      }
      if (started) {
        return {status: 'In Progress', statusColor: 'orange'};
      }
      if (paused) {
        return {status: 'Paused', statusColor: 'blue'};
      }
      return {status: 'Not Started', statusColor: 'grey'};
    },
    getShortTaskFor(auditTask) {
      if (!auditTask || !auditTask.source) {
        return null;
      }
      const {mealId, componentId, line} = auditTask.source;
      return this.shortComponentTasks.find(shortTask =>
        shortTask.source.shortMealId === mealId &&
        shortTask.source.componentId === componentId &&
        shortTask.source.line === line
      );
    },
    isShortTaskNonEditable(component) {
      const shortTask = this.getShortTaskFor(component);
      return shortTask && (shortTask.isStarted || shortTask.complete || shortTask.isPaused);
    },

  },
  computed: {
    ...mapGetters(['getComponent', 'getMeal', 'getDietName', 'getTimeOfDay', 'getRestrictionText', 'getMealIngredients']),
    platingDetailImageUrl() {
      return getPlatingImageUrl(this.platingDetailMeal);
    },
    // chef's choice meals are not menued on a production day, so their own date
    // is no use for looking up the orders
    platingDetailDate() {
      const date = getDateString(this.selectedMeal && this.selectedMeal.date);
      return this.dates.includes(date) ? date : this.dates[0];
    },
    shortPickerTitle() {
      const lineNames = {main: 'Main Line', allergy: 'Allergy Line'};
      return `${lineNames[this.shortLine] || ''} Short Requests`.trim();
    },
    shortComponentTasks() {
      return Object.values(this.componentTaskMap).filter(t => t.source.isShort)
    },
    countMain() {
      return Object.values(this.platingTaskMealMap).length;
    },
    countSelectedMain() {
      return this.mealsWithOrdersFlat.filter(t => this.platingTaskMealMap[t.id]).length;
    },
    countSelectedAllergy() {
      return this.mealsWithOrdersFlat.filter(t => this.allergyPlatingTaskMealMap[t.id]).length;
    },
    countSelectedMainCompleted() {
      return this.mealsWithOrdersFlat.filter(t => this.platingTaskMealMap[t.id] && this.platingTaskMealMap[t.id].complete).length;
    },
    countSelectedAllergyCompleted() {
      return this.mealsWithOrdersFlat.filter(t => this.allergyPlatingTaskMealMap[t.id] && this.allergyPlatingTaskMealMap[t.id].complete).length;
    },
    countMainCompleted() {
      return Object.values(this.platingTaskMealMap).filter(i => i.complete).length;
    },
    countAllergy() {
      return Object.values(this.allergyPlatingTaskMealMap).length;
    },
    countAllergyCompleted() {
      return Object.values(this.allergyPlatingTaskMealMap).filter(i => i.complete).length;
    },
    mealsWithOrders() {
      const result = {};
      for (const date of this.dates) {
        const mealsOnDate = this.meals[date];
        if (mealsOnDate) {
          result[date] = this.meals[date]
            .filter(({id}) => {
              if (this.streamSelect) {
                const platingTask = this.platingTaskMealMap[id];
                if (platingTask && this.streamSelect !== platingTask.source.dietName) {
                  return false;
                }
              }
              return this.getMealCount(id) + this.getAllergyMealCount(id) > 0;
            })

          ;
          result[date] = this.sortMeals(result[date]);
        }
      }
      return result;
    },
    mealsWithOrdersFlat() {
      return Object.values(this.mealsWithOrders).flat();
    },
    datesFormatted() {
      const format = 'ddd MMM D';
      if (!this.dateFrom) {
        return '';
      } else if (this.dateFrom === this.dateTo) {
        return `${moment(this.dateFrom).format(format)}`;
      } else {
        return `${moment(this.dateFrom).format(format)} - ${moment(this.dateTo).format(format)}`
      }
    },
    dateFrom() {
      return [...this.dates].sort()[0];
    },
    dateTo() {
      return [...this.dates].sort().reverse()[0];
    },
    hasDates() {
      return this.dates.length > 0;
    },
    datesWithOrders() {
      return this.dates.filter(date => (this.mealsWithOrders[date] || []).length > 0);
    },
    countRacked() {
      return 0;
    },
    tasks() {
      return [
        ...Object.values(this.platingTaskMealMap),
        ...Object.values(this.allergyPlatingTaskMealMap)
      ];
    },
    mealIds() {
      return Object.values(this.meals).flatMap(meals => meals.map(m => m.id));
    },
    mealMap() {
      return Object.fromEntries(
        Object.values(this.meals).flatMap(meals => meals.map(m => [m.id, m]))
      );
    },
    totalPlatingTime() {
      let time = 0;
      if (!this.loading) {
        this.mealIds.forEach(id => {
          time += this.getPlatingTimeEstimate(this.getMealCount(id));
          time += this.getPlatingTimeEstimate(this.getAllergyMealCount(id));

        });
      }
      return time;
    },
    totalMealCount() {
      //console.log('this.mealIds', this.mealIds);
      let count = 0;
      if (!this.loading) {
        this.mealIds.forEach(id => {
          count += this.getMealCount(id);
          count += this.getAllergyMealCount(id);
        });
      }
      return count;
    },
    overTimeTasks() {
      const overtime = [];
      this.mealIds.forEach(id => {
        // main plating
        if (this.isStarted(id)) {
          const task = this.platingTaskMealMap[id];
          const actual = this.getElapsedTime(task);
          const estimate = this.getPlatingTimeEstimate(this.getMealCount(id));
          if (actual > estimate) {
            overtime.push({
              lateBy: actual - estimate,
              team_members: this.getCurrentTeamMembers(task),
              meal: this.mealMap[id]
            })
          }
        }

        // allergy plating
        if (this.isAllergyStarted(id)) {
          const task = this.allergyPlatingTaskMealMap[id];
          const actual = this.getElapsedTime(task);
          const estimate = this.getPlatingTimeEstimate(this.getAllergyMealCount(id));
          if (actual > estimate) {
            overtime.push({
              lateBy: actual - estimate,
              team_members: this.getCurrentTeamMembers(task),
              meal: this.mealMap[id]
            })
          }
        }
      });
      return overtime.sort((a, b) => a.lateBy - b.lateBy);
    },
    shortCounts() {
      if (!this.shortComponent) {
        return {};
      }
      // blank or cleared inputs count as zero portions; allergy groups add up per size
      const sizes = Object.keys(this.shortComponent.source.count);
      return Object.fromEntries(sizes.map(size => [
        size,
        this.shortGroups.map(g => this.getShortCount(g.key, size)).reduce((sum, n) => sum + n, 0)
      ]));
    },
    totalShortAmount() {
      return this.getShortWeight(this.shortCounts);
    },
    /**
     * Allergy combinations the orders need from the sub component, with the
     * portions ordered per size, e.g. "no beef + no garlic" 1 medium.
     */
    shortOrderAllergyCombos() {
      const c = this.shortComponent;
      if (!c || !this.shortOrders) {
        return [];
      }
      const {mealId, componentId} = c.source;
      const componentFoodIds = (this.getMealIngredients(mealId) || [])
        .filter(i => i.component_id === componentId)
        .map(i => parseInt(i.food_id || i.foodid));
      const combos = {};
      for (const order of this.shortOrders) {
        const detected = filterSelectedIngredients(componentFoodIds, order.allergyDetected) || {};
        const allergies = Object.keys(detected).map(this.getRestrictionText).filter(name => name).sort();
        if (!allergies.length) {
          continue;
        }
        const key = allergies.join(SHORT_ALLERGY_JOIN);
        combos[key] = combos[key] || {key, allergies, ordered: {}};
        combos[key].ordered[order.meal_size] = blankAsZero(combos[key].ordered[order.meal_size]) + order.quantity;
      }
      return Object.values(combos).sort((a, b) => a.key.localeCompare(b.key));
    },
    /**
     * Options for the allergy buttons on a sub short: the combinations the orders
     * need, or, until the orders are known, the single allergies detected in the component.
     */
    shortAllergyOptions() {
      const source = this.shortComponent && this.shortComponent.source;
      if (!source || source.line !== 'S') {
        return [];
      }
      if (this.shortOrders) {
        return this.shortOrderAllergyCombos.map(combo => ({...combo, name: combo.key}));
      }
      return this.allergyNames(source.allergies)
        .filter(name => name)
        .map(name => ({key: name, name, allergies: [name], ordered: null}));
    },
    /**
     * Amount entry groups: one per picked allergy, otherwise a single unnamed group.
     */
    shortGroups() {
      if (this.shortAllergyOptions.length) {
        // the restriction text already reads "no beef", so it is used verbatim
        return this.shortAllergyOptions.map(option => ({...option, name: option.key}));
      }
      return [{key: null, name: ''}];
    },
    activeShortGroup() {
      return this.shortGroups[this.shortAllergyTab] || this.shortGroups[0];
    },
    // portion sizes that were ordered for the component
    shortSizes() {
      const count = this.shortComponent ? this.shortComponent.source.count : {};
      return ['small', 'medium', 'large'].filter(size => count[size] > 0);
    },
    hasShortAmount() {
      return Object.values(this.shortCounts).some(count => count > 0);
    },
    shortTask() {
      const c = this.shortComponent;
      const source = this.shortComponent.source;
      const {mealId, mealName, componentId, line, rack, batch, priority, key, allergies = [], allergyLineAllergy = []} = source;
      const totalKeyMap = {
        M: 'totalAmountMain',
        AL: 'totalAmountAllergy',
        S: 'totalAmountSub'
      }
      const countKeyMap = {
        M: 'count',
        AL: 'allergyCount',
        S: 'substituteCount'
      }
      const counts = {};
      counts[mealId] = {
        "key": `${line}-SHORT`,
        "rack": rack,
      };
      counts[mealId][totalKeyMap[line]] = this.totalShortAmount;
      const count = this.shortCounts;
      counts[mealId][countKeyMap[line]] = count;
      const isSub = line === 'S';
      const shortNote = isSub ? this.shortNote.trim() : '';
      const shortDetails = isSub ? this.shortDetails.trim() : '';
      const shortAllergyCounts = isSub
        ? Object.fromEntries(Object.entries(this.shortAllergyCounts)
          .filter(([key, counts]) => this.shortGroups.some(g => g.key === key)
            && Object.values(counts).some(n => blankAsZero(n) > 0)))
        : {};
      const baseTitle = isSub
        ? `${c.description} (SHORT SUB)`
        : `${c.description} (SHORT ${line})`;
      const title = [baseTitle, Object.keys(shortAllergyCounts).join(', '), shortNote].filter(s => s).join(' - ');
      const countString = (counts) => Object
        .entries(sortBySize(counts))
        .map(([k, v]) => `${k}:${blankAsZero(v)}`)
        .join(' ');
      const weightOf = (counts) => formatWeightWithUnits(this.getShortWeight(counts));
      // e.g. "1 medium, 2 large"
      const portionString = (counts) => Object
        .entries(sortBySize(counts))
        .filter(([, v]) => blankAsZero(v) > 0)
        .map(([k, v]) => `${blankAsZero(v)} ${k}`)
        .join(', ');
      const description = [
        `SHORT for ${line}${rack} of ${key} ${countString(count)} (${weightOf(count)})`,
        ...Object.entries(shortAllergyCounts)
          .map(([key, counts]) => `${weightOf(counts)} with ${key} (${portionString(counts)})`),
        shortDetails
      ].filter(s => s).join('\n');
      const task = {
        title,
        description,
        "reference": `${c.reference}-SHORT`,
        "team_id": 3,  // hard-coded - components team
        "source": {
          mealName,
          componentId,
          isShort: true,
          shortMealId: mealId,
          line,
          rack,
          priority,
          batch,
          "component": {name: c.description},
          requestedAt: new Date().toISOString(),
          allergies, // restriction ids relevant to this line (empty on the main line)
          allergyLineAllergy, // allergy-line allergies not in this component
          count,
          counts,
          totalAmount: this.totalShortAmount,
          shortAllergyCounts, // {allergyName: {size: count}} when the sub short is per allergy
          shortNote,
          shortDetails
          // "totalAmountAllergy": 456,
          // "production_priority": 2
        },
        // "isStarted": false,
        // "isPaused": false,
      }
      task.source[totalKeyMap[line]] = this.totalShortAmount;
      task.from = this.dateFrom;
      task.to = this.dateTo;
      task.team = 'components'
      return task;
    },
    streams() {
      const streamSet = new Set(this.tasks.map(t => t.source.dietName));
      return [...streamSet].sort();
    }
  },
  props: {
    platingMode: {
      type: String,
      default: 'Standard',
      required: false
    },
    readOnly: {type: Boolean, default: true},
  }
}
</script>

<style scoped>
.meal-card {
  border: 4px solid grey;
  border-radius: 6px;
  margin-bottom: 12px;
  padding: 8px;
}

.line-card-col {
  min-width: 0; /* allow the two line cards to share the row even when the buttons are wide */
}

.line-card-actions {
  gap: 6px;
  justify-content: space-between;
}

.short-picker-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.short-picker-allergies {
  margin-top: 4px;
}

.short-picker-request {
  margin-top: 6px;
  font-size: smaller;
}

.short-size {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 8px;
}

.short-size:last-child {
  border-bottom: none;
}

.short-size-label {
  font-weight: bold;
  margin-bottom: 8px;
}

.short-size-value {
  font-size: 1.4em;
  color: #1976d2;
}

.short-notes {
  margin-top: 12px;
}

.short-number-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.short-group {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 6px 0;
}

.short-group-header {
  display: flex;
  align-items: center;
}

.short-allergy-tabs {
  margin-top: 8px;
}

.short-allergy-tabs >>> .v-tab {
  text-transform: none;
}


.short-number-button {
  min-width: 52px !important;
  height: 52px !important;
  font-size: 1.2em;
}

.short-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.short-stepper-input {
  max-width: 7rem;
}

.short-stepper-input >>> input {
  text-align: center;
  font-size: 1.6em;
}

.meal-card-name {
  font-weight: bold;
  font-size: 1.1em;
  text-align: center;
  margin-top: 4px;
}

.meal-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;
}

.meal-card-kitchen {
  flex-shrink: 0;
  margin-left: auto;
}

.meal-card-details {
  font-size: smaller;
  margin-top: 6px;
}

.meal-card-line {
  border-left: 4px solid grey;
  padding-left: 10px;
}

.meal-card-shorts {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 8px;
}

.meal-card-row {
  display: flex;
  align-items: center;
  min-height: 20px;
  line-height: 1.2;
}

.meal-card-heading {
  font-weight: bold;
  justify-content: space-between;
  margin-bottom: 4px;
}

.short-request {
  margin-bottom: 10px;
}

.short-request-description {
  margin-left: 5.5rem; /* line up under the title, past the amount label */
  white-space: pre-line;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.9em;
}

.meal-card-label {
  display: inline-block;
  width: 5.5rem;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.6);
}
</style>